import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import './styles/tokens.css';
import './styles/fonts.css';
import './styles/global.css';

import { App } from '@/app/App';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { bootstrapTheme } from '@/notes/themeStore';
import { bootstrapUI } from '@/notes/uiStore';
import { bootstrapEditorPrefs } from '@/notes/editorPrefs';
import { useTabs } from '@/notes/tabsStore';
import { installPreloadErrorRecovery } from '@/app/preloadRecovery';

// Vite emits this event when an open tab requests a lazy chunk from an older
// deployment. Install the recovery hook before rendering any lazy boundary.
installPreloadErrorRecovery();

async function bootstrap(): Promise<void> {
  // Hydrate theme from Dexie before first paint to avoid a flash when the user
  // has explicitly chosen a non-default theme. Default render is dark — if IDB
  // is unavailable or empty, we stay on dark, which is the default anyway.
  try {
    await Promise.all([bootstrapTheme(), bootstrapUI(), bootstrapEditorPrefs()]);
    // tabsStore.bootstrap depends on Dexie being open + prefs already hydrated.
    await useTabs.getState().bootstrap();
  } catch (err) {
    console.warn('[sveska] bootstrap failed, falling back to defaults:', err);
  }

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('Sveska: #root not found in index.html');

  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StrictMode>,
  );
}

void bootstrap();
